import styled from "@emotion/styled";
import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  ResponsiveContainer,
} from "recharts";
import { getFieldAreaHectares, useFieldsScreenVM } from "@screens/FieldsScreen/FieldsScreenVM";
import { useLang, useFormatArea } from "@src/hooks/useLang";
import Text from "@components/Text";
import SizedBox from "@components/SizedBox";
import Button from "@components/Button";
import { ReactComponent as MinimalizeIcon } from "@assets/icons/minimalize.svg";
import { ReactComponent as ExpandIcon } from "@assets/icons/expand.svg";
import { ReactComponent as DownloadIcon } from "@assets/icons/download.svg";
import LeftBarSettingsMenuItem from "@components/Layout/LeftBar/LeftBarSettingsMenuItem";
import useOnClickOutside from "@src/hooks/useOnClickOutside";
import useIsMobile, { MOBILE_BREAKPOINT } from "@src/hooks/useIsMobile";
// @ts-ignore
import * as FileSaver from "file-saver";
import { useStores } from "@stores";
import { toast } from "react-toastify";
import {
  getCameraDailyData,
  TCameraDailyEntry,
  TCameraMeta,
  TCameraMetric,
} from "@services/fieldsService";

interface IProps {}

// Mirrors MapYieldStatistics' chrome (bottom sheet + minimize/expand + download
// menu), with camera-specific stats/charts as the body. No prescription button.

const Root = styled.div<{ compact?: boolean }>`
  position: absolute;
  left: 24px;
  right: 16px;
  bottom: 16px;
  background: #ffffff;
  border-radius: 6px;
  padding: 16px;
  box-sizing: border-box;
  z-index: 11;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    left: 12px;
    right: 12px;
    /* Sit above the floating "Fields" pill (40px tall, 16px off the tab bar)
       instead of underneath it. */
    bottom: 68px;
    /* Collapsed on a phone the panel is just a title bar — tighten it so it
       doesn't eat the map. */
    padding: ${({ compact }) => (compact ? "10px 12px" : "16px")};
  }
`;

const Title = styled(Text)`
  font-family: "Nunito Sans";
  font-style: normal;
  font-weight: 700;
  font-size: 18px;
  line-height: 26px;
  color: #494c5b;
  white-space: nowrap;

  /* Mobile only: collapsed, the name shares one line with the control buttons,
     so clip it instead of wrapping the panel into a tall block. On desktop the
     parts must keep their natural width — min-width: 0 there lets them shrink
     and truncates the name/area for no reason. */
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const ControlsWrapper = styled.div<{ compact?: boolean }>`
  display: flex;
  flex-direction: row;
  max-width: fit-content;
  position: absolute;
  top: 16px;
  right: 16px;
  & > :first-of-type {
    margin-right: 8px;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    top: ${({ compact }) => (compact ? "10px" : "16px")};
    right: ${({ compact }) => (compact ? "12px" : "16px")};
  }
`;

const ControlButton = styled(Button)`
  height: 24px;
  width: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
`;

const Tooltip = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  position: absolute;
  transition: 0.4s;
  overflow: hidden;
  top: 0;
  background: #ffffff;
  box-shadow: 0 2px 20px rgba(78, 78, 81, 0.2);
  border-radius: 6px 6px 6px 0;
`;

const TitleRow = styled.div<{ compact?: boolean }>`
  display: flex;
  align-items: center;
  width: fit-content;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    /* Collapsed: a single line with just the field name, so it never wraps into
       a multi-line block. */
    flex-wrap: ${({ compact }) => (compact ? "nowrap" : "wrap")};
    width: ${({ compact }) => (compact ? "100%" : "fit-content")};
    min-width: 0;
    overflow: hidden;
    /* Keep the field name clear of the absolutely-pinned control buttons. */
    padding-right: 88px;
  }
`;

const Body = styled.div`
  width: 100%;
`;

/* Stats table — mirrors the yield panel's table layout. */
const TableWrap = styled.div`
  width: 100%;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

const Table = styled.table`
  width: 100%;
  &,
  th,
  td {
    padding: 7px 12px;
    border: 1px solid #ebeeef;
    border-collapse: collapse;
    text-align: left;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    min-width: 560px;
  }
`;

const ChartsRow = styled.div`
  display: flex;
  gap: 24px;
  width: 100%;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
  }
`;

const ChartBox = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  min-width: 0;
`;

const METRIC_BY_VIEW: Record<string, TCameraMetric> = {
  potato_count: "count",
  stone_percent: "stone_percent",
  potato_size: "mean_size",
};

/** "<33", "33–37", …, ">62" labels from the bucket edges. */
const bucketLabels = (meta: TCameraMeta): string[] => {
  const edges = meta?.bucket_edges;
  if (!edges || edges.length === 0) {
    return Array.from({ length: 8 }, (_, i) => String(i + 1));
  }
  const labels = [`<${edges[0]}`];
  for (let i = 0; i < edges.length - 1; i++) {
    labels.push(`${edges[i]}–${edges[i + 1]}`);
  }
  labels.push(`>${edges[edges.length - 1]}`);
  return labels;
};

const cameraExportURL = (
  fieldId: string,
  fieldName: string | undefined,
  format: string,
  metric: TCameraMetric,
  seasonId: string | undefined
): string => {
  const BASE_URL = process.env.REACT_APP_API_URL ?? window.location.origin;
  const q = new URLSearchParams();
  q.append("format", format);
  q.append("metric", metric);
  if (fieldName) q.append("field_name", fieldName.replaceAll(" ", "_"));
  if (seasonId) q.append("seasonId", seasonId);
  return `${BASE_URL}/api/v1/fields/${fieldId}/camera/export?${q.toString()}`;
};

const MapCameraStatistics: React.FC<IProps> = () => {
  const vm = useFieldsScreenVM();
  const lang = useLang();
  const formatArea = useFormatArea();
  const { seasonStore, accountStore } = useStores();

  const ref = useRef(null);
  const isMobile = useIsMobile();
  // On a phone the stats table + charts cover most of the map, so the panel
  // starts collapsed there and opens on demand.
  const [expanded, setExpanded] = useState(!isMobile);
  const [tooltipOpened, setTooltipOpened] = useState(false);
  const [daily, setDaily] = useState<TCameraDailyEntry[]>([]);

  useOnClickOutside(ref, () => setTooltipOpened(false));

  const id = vm.selectedField?.get("id") as string | undefined;
  const name = vm.selectedField?.get("name") as string | undefined;
  const seasonId = seasonStore.currentSeasonId;

  const isCamera = vm.isCameraView(vm.selectedFieldView);
  const metric = METRIC_BY_VIEW[vm.selectedFieldView] ?? "count";
  const stats = id != null ? vm.cameraStatistics[String(id)] : undefined;

  useEffect(() => {
    if (!isCamera || !id || !seasonId) {
      setDaily([]);
      return;
    }
    let cancelled = false;
    getCameraDailyData(id, seasonId).then((res) => {
      if (!cancelled) setDaily(res.days);
    });
    return () => {
      cancelled = true;
    };
  }, [isCamera, id, seasonId]);

  if (vm.isCompareMode || !isCamera || !stats) return null;

  const area = formatArea(getFieldAreaHectares(vm.selectedField)) || null;

  const unit = stats.meta?.size_unit ?? "px";
  const labels = bucketLabels(stats.meta);
  const histData = (stats.histogram ?? []).map((count, i) => ({
    bucket: labels[i] ?? String(i),
    count,
  }));
  const dailyData = daily.map((d) => ({
    date: d.date,
    potatoes: d.potato_count,
    stones: d.stone_count,
  }));

  const t = (key: string, fallback: string): string =>
    (lang.fields as Record<string, string | undefined>)?.[key] ?? fallback;

  const cropId = vm.selectedField?.get("culture_id");
  const crop = vm.crops.find((c) => c.id === cropId);
  // Collapsed on mobile: field name only, next to the control buttons.
  const compact = isMobile && !expanded;
  const titleParts = (compact
    ? [name]
    : [name, area, crop?.name]
  ).filter(Boolean);

  return (
    <Root compact={compact}>
      <ControlsWrapper ref={ref} compact={compact}>
        <Tooltip
          style={{
            right: "0.75em",
            top: tooltipOpened ? "-7em" : 0,
            width: tooltipOpened ? 208 : 0,
            height: tooltipOpened ? "7em" : 0,
          }}
        >
          {[
            { name: "Shapefile", format: "shp", ext: "shp.zip" },
            { name: "John Deere", format: "deere", ext: "deere.zip" },
            { name: "CSV", format: "csv", ext: "csv" },
          ].map((item) => (
            <LeftBarSettingsMenuItem
              key={item.format}
              title={item.name}
              onClick={() => {
                setTooltipOpened(false);
                if (!id) return;
                const url = cameraExportURL(id, name, item.format, metric, seasonId);
                fetch(url)
                  .then((response) => {
                    if (!response.ok) {
                      throw new Error(`Unexpected response status: ${response.status}`);
                    }
                    return response.blob();
                  })
                  .then((blob) => {
                    const safeName = (name ?? "field").replaceAll(" ", "_");
                    FileSaver.saveAs(blob, `${safeName}_${metric}.${item.ext}`);
                  })
                  .catch((e) => {
                    // Raw fetch: nothing else reports a failed export.
                    console.error(e);
                    toast.error(lang.fields.tracksExportFailed);
                  });
              }}
            />
          ))}
        </Tooltip>

        {expanded ? (
          <ControlButton kind="secondary" onClick={() => setExpanded(false)}>
            <MinimalizeIcon />
          </ControlButton>
        ) : (
          <ControlButton kind="secondary" onClick={() => setExpanded(true)}>
            <ExpandIcon />
          </ControlButton>
        )}

        {/* Same gate as the yield panel: no export rights, no export button. */}
        {accountStore.canExport && (
          <ControlButton kind="secondary" onClick={() => setTooltipOpened(!tooltipOpened)}>
            <DownloadIcon />
          </ControlButton>
        )}
      </ControlsWrapper>

      <TitleRow compact={compact}>
        {titleParts.map((part, i) => (
          <React.Fragment key={i}>
            {i > 0 && (
              <Title style={{ color: "rgb(204, 213, 216)" }}>&nbsp;•&nbsp;</Title>
            )}
            <Title>{part}</Title>
          </React.Fragment>
        ))}
      </TitleRow>

      {expanded && (
        <Body>
          <SizedBox height={24} />
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>
                    <Text color="#494C5B" type="heading" size={14}>
                      {t("cameraPotatoTotal", "Potatoes")}
                    </Text>
                  </th>
                  <th>
                    <Text color="#494C5B" type="heading" size={14}>
                      {t("cameraStoneTotal", "Not potatoes")}
                    </Text>
                  </th>
                  <th>
                    <Text color="#494C5B" type="heading" size={14}>
                      {t("cameraStonePercent", "Stone percentage")}
                    </Text>
                  </th>
                  <th>
                    <Text color="#494C5B" type="heading" size={14}>
                      {`${t("cameraMeanSize", "Mean size")}, ${unit}`}
                    </Text>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th>
                    <Text color="#494C5B" type="heading" size={14}>
                      {stats.potato_total.toLocaleString()}
                    </Text>
                  </th>
                  <th>
                    <Text color="#494C5B" type="heading" size={14}>
                      {stats.stone_total.toLocaleString()}
                    </Text>
                  </th>
                  <th>
                    <Text color="#494C5B" type="heading" size={14}>
                      {stats.stone_percent}%
                    </Text>
                  </th>
                  <th>
                    <Text color="#494C5B" type="heading" size={14}>
                      {stats.mean_size != null ? `${stats.mean_size} ${unit}` : "-"}
                    </Text>
                  </th>
                </tr>
              </tbody>
            </Table>
          </TableWrap>
          <SizedBox height={16} />
          <ChartsRow>
            {histData.length > 0 && (
              <ChartBox>
                <Text color="#899093" type="heading" size={12}>
                  {t("cameraSizeDistribution", "Size distribution")} ({unit})
                </Text>
                <SizedBox height={8} />
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={histData} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="bucket" fontSize={11} interval={0} />
                    <YAxis fontSize={11} width={48} />
                    <ChartTooltip />
                    <Bar dataKey="count" fill="#68AD18" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartBox>
            )}
            {dailyData.length > 1 && (
              <ChartBox>
                <Text color="#899093" type="heading" size={12}>
                  {t("cameraDailyCounts", "Daily counts")}
                </Text>
                <SizedBox height={8} />
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={dailyData} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="date" fontSize={11} />
                    <YAxis fontSize={11} width={48} />
                    <ChartTooltip />
                    <Bar dataKey="potatoes" stackId="a" fill="#68AD18" />
                    <Bar dataKey="stones" stackId="a" fill="#A50026" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartBox>
            )}
          </ChartsRow>
        </Body>
      )}
    </Root>
  );
};

export default observer(MapCameraStatistics);
